/**
 * ImageUploadButton — Toolbar button + logic for uploading images into the editor.
 *
 * Flow:
 *   1. User clicks 📷 or drops / pastes an image
 *   2. File validated (type, size)
 *   3. POST to backend → gets SAS upload URL
 *   4. PUT file directly to Azure Blob Storage
 *   5. POST confirm → backend marks attachment active
 *   6. <img> tag inserted at cursor in the editor
 *
 * Fallback: If the backend image endpoints aren't available yet,
 *           the image is embedded as a base64 data-URL so the user
 *           can still work offline or before the endpoints are deployed.
 */
import React, { useRef, useState, useCallback } from 'react';
import { api } from '../../../auth/apiClient';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];

/* ── Validation ─────────────────────────────────────────── */

function validateFile(file) {
    if (!file) return 'No file selected.';
    if (!ALLOWED_TYPES.includes(file.type)) {
        return `Unsupported file type: ${file.type}. Allowed: PNG, JPEG, GIF, WebP, SVG.`;
    }
    if (file.size > MAX_FILE_SIZE) {
        const mb = (file.size / 1024 / 1024).toFixed(1);
        return `File too large (${mb} MB). Maximum is 10 MB.`;
    }
    return null;
}

/* ── Base64 fallback reader ─────────────────────────────── */

function readAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

/* ── Try Azure Blob upload via SAS token ────────────────── */

async function uploadViaBlob(file, companyId, tabId, onProgress) {
    console.log('[uploadViaBlob] Starting upload:', { fileName: file.name, companyId, tabId });

    // 1. Request SAS token from backend
    console.log('[uploadViaBlob] Step 1: Requesting SAS token');
    const tokenRes = await api.post(`/notes/company/${companyId}/images/upload-token`, {
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        tabId,
    });
    console.log('[uploadViaBlob] Token response:', tokenRes);

    const { uploadUrl, blobUrl, attachmentId } = tokenRes.data?.data || tokenRes.data || {};
    console.log('[uploadViaBlob] Extracted:', { uploadUrl: uploadUrl?.slice(0, 50) + '...', blobUrl, attachmentId });

    if (!uploadUrl) {
        console.error('[uploadViaBlob] No uploadUrl in response:', tokenRes);
        throw new Error('No upload URL returned');
    }

    // 2. Upload directly to Azure Blob
    console.log('[uploadViaBlob] Step 2: Uploading to blob storage');
    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
            console.log('[uploadViaBlob] Blob upload XHR complete, status:', xhr.status);
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                console.error('[uploadViaBlob] Blob upload failed with status:', xhr.status, xhr.statusText);
                reject(new Error(`Upload failed: ${xhr.status}`));
            }
        };
        xhr.onerror = () => {
            console.error('[uploadViaBlob] Blob upload network error');
            reject(new Error('Upload network error'));
        };
        xhr.send(file);
    });

    console.log('[uploadViaBlob] Step 3: Confirming upload');
    // 3. Confirm
    const confirmRes = await api.post(`/notes/company/${companyId}/images/${attachmentId}/confirm`, {
        blobUrl,
        tabId,
        fileSize: file.size,
    });

    const confirmData = confirmRes?.data?.data || confirmRes?.data || {};
    const viewUrl = confirmData.viewUrl || blobUrl;

    console.log('[uploadViaBlob] Upload complete:', { blobUrl, viewUrl, attachmentId });
    return { blobUrl: viewUrl, attachmentId };
}

/* ── Component ─────────────────────────────────────────── */

export default function ImageUploadButton({ companyId, tabId, editorRef, onContentChange }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');

    /* Insert <img> at the editors cursor position */
    const insertImage = useCallback((src, alt, attachmentId) => {
        const editor = editorRef?.current;
        if (!editor) return;
        editor.focus();

        const imgHtml =
            `<img src="${src}" alt="${alt || 'image'}" ` +
            `${attachmentId ? `data-attachment-id="${attachmentId}" ` : ''}` +
            `style="max-width:100%;height:auto;display:block;margin:12px 0;border-radius:6px;" ` +
            `loading="lazy" />`;
        document.execCommand('insertHTML', false, imgHtml);
        onContentChange?.();
    }, [editorRef, onContentChange]);

    /* Main upload handler — used by button, paste, and drag-drop */
    const handleFile = useCallback(async (file) => {
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            setTimeout(() => setError(''), 4000);
            return;
        }

        setUploading(true);
        setProgress(0);
        setError('');

        try {
            // Try Azure Blob upload first
            console.log('[ImageUpload] Starting blob upload for:', file.name);
            const { blobUrl, attachmentId } = await uploadViaBlob(file, companyId, tabId, setProgress);
            console.log('[ImageUpload] Blob upload success:', { blobUrl, attachmentId });
            insertImage(blobUrl, file.name, attachmentId);
        } catch (blobErr) {
            console.warn('[ImageUpload] Azure Blob upload failed:', blobErr);
            console.warn('[ImageUpload] Error details:', {
                message: blobErr.message,
                stack: blobErr.stack,
                response: blobErr.response,
            });
            // Fallback: embed as base64
            try {
                console.log('[ImageUpload] Using base64 fallback');
                const dataUrl = await readAsDataUrl(file);
                insertImage(dataUrl, file.name, null);
                console.log('[ImageUpload] Base64 fallback success');
            } catch (readErr) {
                console.error('[ImageUpload] Base64 fallback failed:', readErr);
                setError('Failed to read image file.');
                setTimeout(() => setError(''), 4000);
            }
        } finally {
            setUploading(false);
            setProgress(0);
        }
    }, [companyId, tabId, insertImage]);

    /* File input change */
    const handleFileSelect = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        e.target.value = ''; // allow re-selecting same file
    }, [handleFile]);

    return (
        <>
            {/* Hidden file input */}
            <input
                ref={inputRef}
                type="file"
                accept={ALLOWED_TYPES.join(',')}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />

            {/* Toolbar button */}
            <button
                type="button"
                className="notes-toolbar-btn"
                title="Insert image (upload)"
                disabled={uploading}
                onMouseDown={(e) => {
                    e.preventDefault();
                    inputRef.current?.click();
                }}
            >
                {uploading ? `${progress}%` : '🖼️'}
            </button>

            {/* Inline error toast */}
            {error && (
                <span className="img-upload-error" title={error}>
                    ⚠ {error.length > 30 ? error.slice(0, 30) + '…' : error}
                </span>
            )}
        </>
    );
}

/* Exported utility so NoteEditor can call it for paste / drag-drop */
export { validateFile, readAsDataUrl, uploadViaBlob };
